// Flow Configuration - Screen definitions and navigation
import { isScreenEnabledForVariant } from './experiments';

// A/B face_scan: the whole scan sequence exists only in the with-scan arm.
// The no_scan_treatment arm flows straight from the quiz into results.
const FACE_SCAN_GATE = { experiment: 'face_scan', variants: ['with_scan_control'] };

export const FLOW_CONFIG = {
  initialScreen: 'age',
  // Phase 1: Discovery and Personalization
  phase1: [
    {
      id: 'age',
      hash: '#age',
      template: 'screen-age',
      dataField: 'wq_age_range',
      showHeader: false,
      preload: { when: 'onload', what: 'images' },
      analytics: { screen_name: 'age_selection' }
    },
    {
      id: 'research-fact',
      hash: '#research-fact',
      template: 'screen-research-fact',
      analytics: { screen_name: 'research_fact' }
    },
    {
      id: 'primary-goal',
      hash: '#primary-goal',
      template: 'screen-primary-goal',
      dataField: 'wq_primary_goal',
      preload: { when: {type: 'screens-before', count: 1}, what: 'images' },
      analytics: { screen_name: 'primary_goal_selection' }
    },
    {
      id: 'additional-concerns',
      hash: '#additional-concerns',
      template: 'screen-additional-concerns',
      dataField: 'wq_additional_concerns',
      multiSelect: true,
      preload: { when: {type: 'screens-before', count: 1}, what: 'images' },
      analytics: { screen_name: 'additional_concerns_selection' }
    },
    // {
    //   id: 'social-proof',
    //   hash: '#social-proof',
    //   template: 'screen-social-proof',
    //   analytics: { screen_name: 'social_proof_interstitial' }
    // },
    {
      id: 'skin-type',
      hash: '#skin-type',
      template: 'screen-skin-type',
      dataField: 'wq_skin_type',
      preload: { when: {type: 'screens-before', count: 2}, what: 'images' },
      analytics: { screen_name: 'skin_type_selection' }
    },
    {
      id: 'not-alone',
      hash: '#not-alone',
      template: 'screen-not-alone',
      preload: { when: 'afterAppLoaded', what: 'globe' },
      analytics: { screen_name: 'not_alone_interstitial' }
    },
    {
      id: 'ready-35',
      hash: '#ready_35',
      template: 'screen-ready-35',
      analytics: { screen_name: 'ready_35_progress' }
    },
    {
      id: 'routine-consistency',
      hash: '#routine-consistency',
      template: 'screen-routine-consistency',
      dataField: 'wq_routine_consistency',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'routine_consistency_selection' }
    },
    {
      id: 'current-products',
      hash: '#current-products',
      template: 'screen-current-products',
      dataField: 'wq_current_products',
      multiSelect: true,
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'current_products_selection' }
    },
    {
      id: 'smart-investment',
      hash: '#smart-investment',
      template: 'screen-smart-investment',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'smart_investment_info' }
    },
    {
      id: 'retinol',
      hash: '#retinol',
      template: 'screen-retinol',
      dataField: 'wq_retinol_experience',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'retinol_experience_selection' }
    },
    {
      id: 'parabens',
      hash: '#parabens',
      template: 'screen-parabens',
      dataField: 'wq_paraben_preference',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'paraben_preference_selection' }
    },
    {
      id: 'acids',
      hash: '#acids',
      template: 'screen-acids',
      dataField: 'wq_acid_preference',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'acid_preference_selection' }
    },
    {
      id: 'antioxidant',
      hash: '#antioxidant',
      template: 'screen-antioxidant',
      dataField: 'wq_antioxidant_usage',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'antioxidant_usage_selection' }
    },
    {
      id: 'ready-67',
      hash: '#ready_67',
      template: 'screen-ready-67',
      analytics: { screen_name: 'ready_67_progress' }
    },
    {
      id: 'moisturizing',
      hash: '#moisturizing',
      template: 'screen-moisturizing',
      dataField: 'wq_hydration_level',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'moisturizing_selection' }
    },
    {
      id: 'sensitivity',
      hash: '#sensitivity',
      template: 'screen-sensitivity',
      dataField: 'wq_sensitivity_level',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'sensitivity_selection' }
    },
    {
      id: 'ready-89',
      hash: '#ready_89',
      template: 'screen-ready-89',
      analytics: { screen_name: 'ready_89_progress' }
    },
    {
      id: 'sleeping',
      hash: '#sleeping',
      template: 'screen-sleeping',
      dataField: 'wq_sleep_duration',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'sleep_duration_selection' }
    },
    {
      id: 'water',
      hash: '#water',
      template: 'screen-water',
      dataField: 'wq_water_intake',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'water_intake_selection' }
    },
    {
      id: 'stress',
      hash: '#stress',
      template: 'screen-stress',
      dataField: 'wq_stress_level',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'stress_level_selection' }
    },
    {
      id: 'routine-duration',
      hash: '#routine-duration',
      template: 'screen-routine-duration',
      dataField: 'wq_routine_duration',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'routine_duration_selection' }
    },
    // {
    //   id: 'sun-behavior',
    //   hash: '#sun-behavior',
    //   template: 'screen-sun-behavior',
    //   dataField: 'sun_behavior',
    //   analytics: { screen_name: 'sun_behavior_selection' }
    // },
    // {
    //   id: 'routine-complexity',
    //   hash: '#routine-complexity',
    //   template: 'screen-routine-complexity',
    //   dataField: 'routine_complexity',
    //   analytics: { screen_name: 'routine_complexity_selection' }
    // },
    // {
    //   id: 'spf-usage',
    //   hash: '#spf-usage',
    //   template: 'screen-spf-usage',
    //   dataField: 'spf_usage',
    //   analytics: { screen_name: 'spf_usage_selection' }
    // },
    // {
    //   id: 'makeup-frequency',
    //   hash: '#makeup-frequency',
    //   template: 'screen-makeup-frequency',
    //   dataField: 'makeup_frequency',
    //   analytics: { screen_name: 'makeup_frequency_selection' }
    // },
    {
      id: 'harvard-fact',
      hash: '#harvard-fact',
      template: 'screen-harvard-fact',
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'harvard_fact' }
    },
    {
      id: 'ready-96',
      hash: '#ready_96',
      template: 'screen-ready-96',
      analytics: { screen_name: 'ready_96_progress' }
    },
    {
      id: 'app-goals',
      hash: '#app-goals',
      template: 'screen-app-goals',
      dataField: 'wq_app_goals',
      multiSelect: true,
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'app_goals_selection' }
    },
    {
      id: 'mood',
      hash: '#mood',
      template: 'screen-mood',
      dataField: 'wq_mood_goals',
      multiSelect: true,
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'mood_goals_selection' }
    },
    {
      id: 'motivation',
      hash: '#motivation',
      template: 'screen-motivation',
      dataField: 'wq_skincare_aspiration',
      multiSelect: true,
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'skincare_aspiration_selection' }
    },
    // {
    //   id: 'medical',
    //   hash: '#medical',
    //   template: 'screen-medical',
    //   dataField: 'medical_considerations',
    //   multiSelect: true,
    //   analytics: { screen_name: 'medical_considerations_selection' }
    // },
    // {
    //   id: 'ingredients',
    //   hash: '#ingredients',
    //   template: 'screen-ingredients',
    //   dataField: 'excluded_ingredients',
    //   multiSelect: true,
    //   analytics: { screen_name: 'ingredient_exclusions_selection' }
    // },
    // {
    //   id: 'budget',
    //   hash: '#budget',
    //   template: 'screen-budget',
    //   dataField: 'monthly_budget',
    //   analytics: { screen_name: 'budget_selection' }
    // },
    // {
    //   id: 'location',
    //   hash: '#location',
    //   template: 'screen-location',
    //   dataField: 'location',
    //   analytics: { screen_name: 'location_input' }
    // },
    // {
    //   id: 'lifestyle',
    //   hash: '#lifestyle',
    //   template: 'screen-lifestyle',
    //   dataField: 'lifestyle_factors',
    //   multiSelect: true,
    //   analytics: { screen_name: 'lifestyle_factors_selection' }
    // },
    // {
    //   id: 'allergies',
    //   hash: '#allergies',
    //   template: 'screen-allergies',
    //   next: 'processing',
    //   back: 'lifestyle',
    //   dataField: 'allergies_intolerances',
    //   optional: true,
    //   analytics: { screen_name: 'allergies_input' }
    // },
    // {
    //   id: 'processing',
    //   hash: '#processing',
    //   template: 'screen-processing',
    //   duration: 8000, // 8 seconds
    //   analytics: { screen_name: 'processing_analysis' }
    // }
  ],

  // Phase 2: Results Generation and Conversion
  phase2: [
    {
      id: 'selfie-preintro',
      hash: '#selfie-preintro',
      template: 'screen-selfie-preintro',
      variantGate: FACE_SCAN_GATE,
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'selfie_preintro' }
    },
    {
      id: 'selfie-intro',
      hash: '#selfie-intro',
      template: 'screen-selfie-intro',
      variantGate: FACE_SCAN_GATE,
      preload: { when: {type: 'screens-before', count: 3}, what: 'images' },
      analytics: { screen_name: 'selfie_introduction' }
    },
    {
      id: 'camera',
      hash: '#camera',
      template: 'screen-camera',
      variantGate: FACE_SCAN_GATE,
      dataField: 'selfie',
      requiresCamera: true,
      permissionDeniedSkipTo: 'results',
      showHeader: false,
      // Seconds the "Continue (N)" button counts down before auto-advancing once the scan completes
      autoContinueCountdown: 5,
      preload: { when: {type: 'screens-before', count: 20}, what: 'camera-detection' },
      analytics: { screen_name: 'camera_capture' }
    },
    {
      id: 'analysis',
      hash: '#analysis',
      template: 'screen-analysis',
      variantGate: FACE_SCAN_GATE,
      duration: 15000, // 15 seconds
      analytics: { screen_name: 'ai_analysis_loading' }
    },
    {
      id: 'results',
      hash: '#results',
      template: 'screen-results',
      analytics: { screen_name: 'results_summary' }
    },
    // {
    //   id: 'plan',
    //   hash: '#plan',
    //   template: 'screen-plan',
    //   next: 'prediction',
    //   back: 'results',
    //   analytics: { screen_name: 'action_plan_preview' }
    // },
    // {
    //   id: 'prediction',
    //   hash: '#prediction',
    //   template: 'screen-prediction',
    //   analytics: { screen_name: 'goal_state_prediction' }
    // },
    {
      id: 'email',
      hash: '#email',
      template: 'screen-email',
      dataField: 'email',
      required: true,
      validation: 'email',
      analytics: { screen_name: 'email_capture' }
    },
  ],

  // Phase 3: Conversion and Paywall
  phase3: [
    {
      id: 'paywall',
      hash: '#checkout',
      template: 'screen-paywall',
      showHeader: false,
      timer: 600, // 10 minutes in seconds
      preload: { when: {type: 'screens-before', count: 5}, what: 'images' },
      analytics: { screen_name: 'paywall' }
    },
    {
      id: 'success',
      hash: '#success',
      template: 'screen-success',
      showHeader: false,
      analytics: { screen_name: 'success' }
    }
  ],

  // Get all screens in order (A/B-gated screens filtered by the visitor's arm,
  // so navigation, progress and preloading all follow the assigned flow)
  getAllScreens() {
    return [...this.phase1, ...this.phase2, ...this.phase3]
      .filter(isScreenEnabledForVariant);
  },

  // Get screen by ID
  getScreenById(id) {
    return this.getAllScreens().find(screen => screen.id === id);
  },

  // Get screen by hash
  getScreenByHash(hash) {
    return this.getAllScreens().find(screen => screen.hash === hash);
  },

  // Get next screen
  getNextScreen(currentId) {
    const current = this.getScreenById(currentId);
    if (!current) return null;

    // If next is explicitly defined, use it
    if (current.next !== undefined) {
      return current.next ? this.getScreenById(current.next) : null;
    }

    // Otherwise, use the next item in the list
    const screens = this.getAllScreens();
    const currentIndex = screens.findIndex(screen => screen.id === currentId);
    if (currentIndex >= 0 && currentIndex < screens.length - 1) {
      return screens[currentIndex + 1];
    }
    return null;
  },

  // Get previous screen
  getPreviousScreen(currentId) {
    const current = this.getScreenById(currentId);
    if (!current) return null;

    // If back is explicitly defined, use it
    if (current.back !== undefined) {
      return current.back ? this.getScreenById(current.back) : null;
    }

    // Otherwise, use the previous item in the list
    const screens = this.getAllScreens();
    const currentIndex = screens.findIndex(screen => screen.id === currentId);
    if (currentIndex > 0) {
      return screens[currentIndex - 1];
    }
    return null;
  },

  // Get total screen count
  getTotalScreens() {
    return this.getAllScreens().length;
  },

  // Get screen index
  getScreenIndex(id) {
    const screens = this.getAllScreens();
    return screens.findIndex(screen => screen.id === id) + 1;
  }
};